'use client';

import { Logo } from '@/components/layout/system/logo';
import { DialogTitle } from '@/components/ui/overlay/dialog';
import { LocaleLink } from '@/i18n/navigation';
import {
  readAuthSessionTransitionDirection,
  useAuthSessionObserver,
} from '@/lib/auth/auth-session-observer';
import { cn } from '@/lib/ui/utils';
import { Routes } from '@/routes';
import { type ReactNode, type RefObject, useEffect, useState } from 'react';
import type { CmsLoginDialogEditTargets } from './cms-login-dialog';
import type { CmsLoginDialogModel } from './cms-login-dialog-model';
import styles from './cms-login-dialog.module.css';

export function CmsLoginDialogBody({
  dialogTitleRef,
  editTargets,
  googleButtonLoading,
  model,
  phase = 'sign-in',
  renderAuthFlow,
  title,
  transitionTitle,
}: {
  dialogTitleRef?: RefObject<HTMLHeadingElement | null>;
  editTargets?: CmsLoginDialogEditTargets;
  googleButtonLoading?: boolean;
  model: CmsLoginDialogModel;
  phase?: 'sign-in' | 'preparing-workspace';
  renderAuthFlow: (params: {
    onTitleChange: (title: string) => void;
  }) => ReactNode;
  title?: ReactNode;
  transitionTitle?: string;
}) {
  const [activeTitle, setActiveTitle] = useState(model.title);
  const authSession = useAuthSessionObserver();
  const isPreparingWorkspace = phase === 'preparing-workspace';
  const isSignInTransitionActive =
    readAuthSessionTransitionDirection(authSession) === 'sign-in';
  const titleState = isPreparingWorkspace
    ? 'preparing-workspace'
    : isSignInTransitionActive
      ? 'authenticating'
      : 'sign-in';
  const showTransitionTitle = titleState !== 'sign-in';

  useEffect(() => {
    setActiveTitle(model.title);
  }, [model.id, model.title]);

  return (
    <section
      aria-busy={googleButtonLoading || showTransitionTitle || undefined}
      className={styles.body}
      data-cms-login-dialog-phase={showTransitionTitle ? titleState : undefined}
    >
      <div className={styles.header}>
        <div className={styles.logoShell} aria-hidden="true">
          <Logo className={styles.logo} />
        </div>
        {title ??
          (dialogTitleRef ? (
            <DialogTitle
              className={cn(
                styles.title,
                transitionTitle ? styles.titleTransition : undefined
              )}
              data-cms-login-dialog-title-state={titleState}
              ref={dialogTitleRef}
              tabIndex={-1}
              {...(!showTransitionTitle && activeTitle === model.title
                ? editTargets?.title
                : {})}
            >
              <CmsLoginDialogTitleLayers
                activeTitle={activeTitle}
                showTransitionTitle={showTransitionTitle}
                transitionTitle={transitionTitle}
              />
            </DialogTitle>
          ) : (
            <div
              className={cn(
                styles.title,
                transitionTitle ? styles.titleTransition : undefined
              )}
              data-cms-login-dialog-title-state={titleState}
              {...(!showTransitionTitle && activeTitle === model.title
                ? editTargets?.title
                : {})}
            >
              <CmsLoginDialogTitleLayers
                activeTitle={activeTitle}
                showTransitionTitle={showTransitionTitle}
                transitionTitle={transitionTitle}
              />
            </div>
          ))}
      </div>

      {renderAuthFlow({ onTitleChange: setActiveTitle })}

      <div className={styles.legal}>
        {model.legalPrefix.trimEnd()}{' '}
        <LocaleLink href={Routes.TermsOfService} className={styles.legalLink}>
          {model.termsLabel}
        </LocaleLink>{' '}
        &amp;{' '}
        <LocaleLink href={Routes.PrivacyPolicy} className={styles.legalLink}>
          {model.privacyLabel}
        </LocaleLink>
        .
      </div>
    </section>
  );
}

function CmsLoginDialogTitleLayers({
  activeTitle,
  showTransitionTitle,
  transitionTitle,
}: {
  activeTitle: string;
  showTransitionTitle: boolean;
  transitionTitle?: string;
}) {
  if (!transitionTitle) {
    return activeTitle;
  }

  return (
    <>
      <span
        aria-hidden={showTransitionTitle}
        className={cn(
          styles.titleTransitionLayer,
          showTransitionTitle
            ? styles.titleTransitionLayerHidden
            : styles.titleTransitionLayerVisible
        )}
      >
        {activeTitle}
      </span>
      <span
        aria-hidden={!showTransitionTitle}
        className={cn(
          styles.titleTransitionLayer,
          showTransitionTitle
            ? styles.titleTransitionLayerVisible
            : styles.titleTransitionLayerHidden,
          showTransitionTitle ? styles.titleTransitionLayerBreathing : undefined
        )}
        data-cms-login-dialog-title-animation={
          showTransitionTitle ? 'breathing' : undefined
        }
      >
        {transitionTitle}
      </span>
    </>
  );
}
